import React from 'react';
import dynamic from 'next/dynamic';
import { Col, Container, Row } from 'react-bootstrap';
import { FaCircleQuestion, FaHeadset } from 'react-icons/fa6';
import { http } from '../helpers';
import Seo from '@/components/seo';

export default function WhyUsePage({ videos }) {
  const Faq = dynamic(() => import('@/components/site/faq'));

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Executei uma consulta, mas, ainda não recebi o e-mail com o link do meu relatório?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Por favor, verifique na seção "Spam" do seu e-mail. Não é algo comum, mas algumas vezes acontece de um desses e-mails acabar parando lá. Você também pode resgatar seu relatório diretamente pelo nosso site.'
        }
      },
      {
        '@type': 'Question',
        'name': 'O relatório da minha consulta deu erro e/ou não foi gerado! O que devo fazer?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Pode ficar tranquilo, o crédito não foi debitado de sua conta em nossa plataforma e você pode utilizá-lo novamente.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Meu relatório está apresentando uma mensagem dizendo que algumas informações ainda estão sendo processadas. O que faço?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Não se preocupe, assim que recebermos essas informações de nossos fornecedores parceiros, elas serão atualizadas no seu relatório.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Meu relatório não retornou o RENAVAM, Número do Motor ou Chassi do veículo. O que faço?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Em poucos casos, ocorre de informações ficarem pendentes ou realmente não existirem no cadastro do veículo. Nossa equipe de atendimento está pronta para atuar em situações como esta e atualizar seu laudo. Entre em contato por contato@aboutcar.com.br.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Não apareceu o Histórico de KM no relatório da minha consulta. Por quê?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'O Histórico de KM é baseado em dados fornecidos por nossa rede de parceiros e só será exibido caso haja registros consistentes para a placa consultada.'
        }
      }
    ]
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://aboutcar.com.br/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Dúvidas Frequentes',
        item: 'https://aboutcar.com.br/duvidas-frequentes'
      }
    ]
  };

  return (
    <>
      <Seo
        title="Dúvidas Frequentes sobre Consulta Veicular | AboutCar"
        description="Tire suas dúvidas sobre consultas veiculares, relatórios, resgate de laudos, histórico de quilometragem, leilão e muito mais na FAQ da AboutCar."
        keywords="faq consulta veicular, duvidas relatorio veicular, suporte consulta placa, perguntas frequentes sobre carro"
        schema={[faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="bg-light-subtle border-bottom py-5">
        <Container className="py-2">
          <Row className="align-items-center g-4">
            <Col lg={7}>
              <span className="badge bg-primary text-white rounded-pill px-3 py-1 text-uppercase fw-bold mb-3 fs-7">
                AboutCar + Suporte
              </span>
              <h1 className="display-4 fw-extrabold text-dark mb-3">
                Dúvidas <span className="text-primary">Frequentes</span>
              </h1>
              <p className="lead text-secondary mb-4">
                Encontre respostas rápidas e claras para as perguntas mais comuns sobre relatórios veiculares, prazos, formas de pagamento e suporte.
              </p>

              <Row className="g-3">
                <Col sm={6}>
                  <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle d-flex align-items-center gap-3">
                    <div className="bg-primary-subtle text-primary p-2 rounded-3">
                      <FaCircleQuestion className="fs-4" />
                    </div>
                    <div>
                      <span className="d-block text-muted small fw-semibold">Central de Ajuda</span>
                      <strong className="text-dark small">Respostas Objetivas</strong>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle d-flex align-items-center gap-3">
                    <div className="bg-success-subtle text-success p-2 rounded-3">
                      <FaHeadset className="fs-4" />
                    </div>
                    <div>
                      <span className="d-block text-muted small fw-semibold">Atendimento Humano</span>
                      <strong className="text-dark small">Suporte via WhatsApp</strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>

            <Col lg={5} className="text-center">
              <img src="/assets/images/contact-us.svg" className="img-fluid max-h-350" border="0" alt="FAQ AboutCar" loading="lazy" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Accordion Section */}
      <Container className="py-5">
        <Faq items={videos} />
      </Container>
    </>
  );
}

export async function getServerSideProps() {
  let {
    videos,
  } = {};
  const resVideos = await http.baseAsync('GET', '/videos');
  if (resVideos.ok) {
    videos = await resVideos.json();
  }
  return {
    props: {
      videos,
    },
  };
}
